// ActiveGameRow.js
// Shared Pick Up game row — web port of ActiveGameRow (RosterBattlesView.swift).
// Used by both the Roster Battles landing page and the Pick Up hub.
//
// Shows the sport tile, "vs <opponent>", a live H:MM turn timer (or the sport name when
// completed), and stacked status/picks pills. Border + timer color reflect whose turn it is.

import React, { useState, useEffect } from "react";
import footballIcon from "../assets/football.svg";
import baseballIcon from "../assets/baseball-fill.svg";
import basketballIcon from "../assets/dribbble-logo-fill.svg";
import hockeyIcon from "../assets/hockey-fill.svg";
import soccerIcon from "../assets/soccer-ball-fill.svg";
import { isUserTurn as isUserTurnFn, opponentUsername, totalPicks } from "../managers/pickupService";

const APP_THEME = "#d4f53c";

// Shared sport metadata (hex + glyph + display name). Exported so the hub and Roster Battles
// render identical sport icons without redefining the map.
export const SPORT_META = {
  football: { hex: "#00b04f", icon: footballIcon, label: "Football" },
  baseball: { hex: "#db2626", icon: baseballIcon, label: "Baseball" },
  basketball: { hex: "#ff8c00", icon: basketballIcon, label: "Basketball" },
  hockey: { hex: "#0da6e8", icon: hockeyIcon, label: "Hockey" },
  soccer: { hex: "#1a66e6", icon: soccerIcon, label: "Soccer" },
};
export const SPORT_ORDER = ["football", "baseball", "basketball", "hockey", "soccer"];

// Sport-colored glyph via CSS mask (same technique as RosterBattles).
export function maskStyle(icon, color, size) {
  return {
    width: size,
    height: size,
    background: color,
    WebkitMaskImage: `url(${icon})`,
    maskImage: `url(${icon})`,
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
    WebkitMaskSize: "contain",
    maskSize: "contain",
  };
}

function fmtRemaining(expiresAt, now) {
  const remaining = Math.max(0, Math.floor((expiresAt.getTime() - now) / 1000));
  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}`;
}

export default function ActiveGameRow({ game, currentUserID, isCompleted = false, onTap }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (isCompleted) return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(t);
  }, [isCompleted]);

  const meta = SPORT_META[game.sport] || SPORT_META.football;
  const yourTurn = isUserTurnFn(game, currentUserID);
  const timerColor = yourTurn ? APP_THEME : "rgba(255,255,255,0.3)";
  const picks = game.picks.length;
  const total = totalPicks(game);

  const borderColor = isCompleted
    ? `${APP_THEME}80`
    : yourTurn
    ? `${meta.hex}73`
    : `${meta.hex}38`;

  return (
    <button onClick={onTap} style={{ ...styles.row, borderColor }}>
      {/* Sport tile */}
      <span style={{ ...styles.tile, background: `${meta.hex}1f` }}>
        <span style={maskStyle(meta.icon, meta.hex, 18)} />
      </span>

      {/* Opponent + timer/sport */}
      <span style={styles.middle}>
        <span style={styles.vs}>vs {opponentUsername(game, currentUserID)}</span>
        {isCompleted ? (
          <span style={styles.sub}>{meta.label}</span>
        ) : (
          <span style={{ ...styles.subTimer, color: timerColor }}>
            ⏱ {fmtRemaining(game.turnExpiresAt, now)}
          </span>
        )}
      </span>

      {/* Stacked pills */}
      <span style={styles.pills}>
        {isCompleted ? (
          <span style={{ ...styles.pill, background: APP_THEME, color: "#0c0d10" }}>RESULTS</span>
        ) : yourTurn ? (
          <span style={{ ...styles.pill, background: APP_THEME, color: "#0c0d10" }}>YOUR TURN</span>
        ) : (
          <span style={{ ...styles.pill, background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.35)" }}>
            WAITING
          </span>
        )}
        <span style={{ ...styles.pill, background: `${meta.hex}26`, color: meta.hex }}>
          {picks}/{total} PICKS
        </span>
      </span>
    </button>
  );
}

const styles = {
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    textAlign: "left",
    padding: 10,
    borderRadius: 14,
    background: "#12151f",
    border: "1px solid",
    cursor: "pointer",
  },
  tile: { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  middle: { display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 0 },
  vs: { fontSize: 14, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  sub: { fontSize: 11, fontWeight: 500, color: "rgba(255,255,255,0.4)" },
  subTimer: { fontSize: 11, fontWeight: 600 },
  pills: { display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 },
  pill: { width: 84, textAlign: "center", padding: "5px 0", borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: 0.4 },
};
